import { gql } from '@apollo/client/core'
import {
  ArcConvertedFileDto,
  ArcConvertFileDto,
  ArcSite,
  BookDto,
  BookGetArgs, BookGetByIdArgs,
  BookGetTagsArgs,
  BookPagedResultDto,
  BookAppleBooksCodeDto,
  BookTagDto, bsConstants,
  CreateBookDto, EntityDeletionSummaryDto, EpubPreviewDto, GenerateEpubPreviewDto, HasIndex,
  UpdateBookDto,
  validStr
} from 'booksprout'
import authStoreGetters from '../authentication/store/getters'
import { useApollo } from '../apollo/apolloModule'
import { useAxios, withoutLoader } from '../axios/axiosModule'
import { useBaseCrud } from '../../composables/useBaseCrud'

/**
 * Replaces `BookService`, the largest of the domain services.
 *
 * `bookFragments` is exported: `arc.service` interpolates the book fragment into one of its queries and
 * used to reach it by injecting the whole BookService for that one property.
 *
 * This is the only service that overrides `validateAddPictureFactory` — book covers must be at least
 * 415px tall — which is why `useBaseCrud` takes it as an option. It has to be declared before the
 * `useBaseCrud` call that receives it, since that call runs at module scope.
 */

export const bookFragments = {
  book: gql`
    fragment BookFragment on BookDto {
      id,
      title,
      description,
      penName,
      authorPenNameId,
      authorPenName {
        id,
        name,
        image,
        averageRating,
        followers {
          id
        }
      },
      penNameType,
      bookCover,
      releaseDate,
      seriesName,
      seriesNumber,
      categories {
        id,
        name,
        translations {
          locale
          name
        }
        parentCategory {
          id,
          name
        }
      },
      keywords,
      wordCount,
      audience,
      language,
      linkAmazonAsin,
      linkAmazonAsinPrint,
      linkAudibleUs,
      linkAudibleUk,
      linkBarnes,
      linkBookbub,
      linkGoodreads,
      linkGoogle,
      linkITunes,
      linkKobo,
      linkSmashwords,
      smashwordsCouponCode,
      linkITunesAudio,
      linkBarnesAudio,
      linkGoogleAudio,
      linkGoodreadsAudio,
      linkKoboAudio,
      linkBookbubAudio,
      linkStoryGraph,
      linkStoryGraphAudio,
      isDraft,
      averageRating,
      reviewCount,
      lastArcOn,
      newReviewCount,
      updatedReviewCount,
      importedId,
      pdfFile,
      pdfFileSizeBytes,
      pdfSystemGenerated,
      pdfFileDate,
      pdfFileFriendlyName,
      epubFile,
      epubFileSizeBytes,
      epubSystemGenerated,
      epubPreviewFile,
      epubFileDate,
      epubFileFriendlyName
    }
  `,

  appleBooksCode: gql`
    fragment BookAppleBooksCodeFragment on BookAppleBooksCodeDto {
      id,
      code,
      expirationDate,
      claimed
    }
  `
}

/**
 * Book covers must be at least 415px tall. `model` is part of the shared signature — the rule only
 * looks at the file — and `useBaseCrud` calls this through its option hook.
 */
const validateAddPictureFactory = (files: any[], _model?: BookDto): Promise<boolean> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.readAsDataURL(files[0])
    reader.onload = function (e: any) {
      const image = new Image()
      image.src = e.target.result
      image.onload = function () {
        const height = image.height
        if (height < 415) {
          return reject('invalidBookCoverSize')
        }
        resolve(true)
      }
    }
  })
}

const crud = useBaseCrud<BookDto>('book', { validateAddPictureFactory })

const sanitizeBookCategories = <TCategory extends {
  id?: number
  parentCategoryId?: number | null
  parentCategory?: { id?: number } | null
}> (categories?: TCategory[]): TCategory[] | undefined => {
  return categories?.filter(category => {
    // This helper is used with both full category objects and id-only mutation inputs.
    // Only treat a category as top-level when parent metadata is explicitly present and null;
    // if the field is absent entirely, keep it because we don't have enough information here.
    if (Object.prototype.hasOwnProperty.call(category, 'parentCategoryId')) {
      return typeof category.parentCategoryId === 'number'
    }

    if (Object.prototype.hasOwnProperty.call(category, 'parentCategory')) {
      return typeof category.parentCategory?.id === 'number'
    }

    return true
  })
}

const baseBookVars = (model: CreateBookDto | UpdateBookDto) => {
  const categories = sanitizeBookCategories(model.categories)

  return {
    title: model.title,
    description: model.description,
    penName: model.penName,
    authorPenNameId: model.authorPenNameId,
    penNameType: model.penNameType,
    bookCover: model.bookCover,
    releaseDate: model.releaseDate,
    seriesName: model.seriesName,
    seriesNumber: model.seriesNumber,
    categories: categories?.map(c => ({ id: c.id })),
    keywords: model.keywords,
    wordCount: model.wordCount,
    audience: model.audience,
    language: model.language,
    linkAmazonAsin: model.linkAmazonAsin,
    linkAmazonAsinPrint: model.linkAmazonAsinPrint,
    linkGoodreads: model.linkGoodreads,
    linkBookbub: model.linkBookbub,
    linkBarnes: model.linkBarnes,
    linkGoogle: model.linkGoogle,
    linkKobo: model.linkKobo,
    linkSmashwords: model.linkSmashwords,
    smashwordsCouponCode: model.smashwordsCouponCode,
    linkITunes: model.linkITunes,
    linkAudibleUs: model.linkAudibleUs,
    linkAudibleUk: model.linkAudibleUk,
    linkITunesAudio: model.linkITunesAudio,
    linkBarnesAudio: model.linkBarnesAudio,
    linkGoogleAudio: model.linkGoogleAudio,
    linkGoodreadsAudio: model.linkGoodreadsAudio,
    linkKoboAudio: model.linkKoboAudio,
    linkBookbubAudio: model.linkBookbubAudio,
    linkStoryGraph: model.linkStoryGraph,
    linkStoryGraphAudio: model.linkStoryGraphAudio,
    pdfFile: model.pdfFile,
    pdfFileSizeBytes: model.pdfFileSizeBytes,
    pdfSystemGenerated: model.pdfSystemGenerated,
    pdfFileDate: model.pdfFileDate,
    pdfFileFriendlyName: model.pdfFileFriendlyName,
    epubFile: model.epubFile,
    epubFileSizeBytes: model.epubFileSizeBytes,
    epubSystemGenerated: model.epubSystemGenerated,
    epubPreviewFile: model.epubPreviewFile,
    epubFileDate: model.epubFileDate,
    epubFileFriendlyName: model.epubFileFriendlyName
  }
}

const getBookResult = (book: BookDto): BookDto | null => {
  if (book === null || book === void 0) return null

  const result = {
    ...book,
    categories: sanitizeBookCategories(book.categories),
    authorPenNameId: book.authorPenName?.id,
    releaseDate: book.releaseDate ? new Date(book.releaseDate) : void 0,
    pdfFileDate: book.pdfFileDate ? new Date(book.pdfFileDate) : void 0,
    epubFileDate: book.epubFileDate ? new Date(book.epubFileDate) : void 0,
  }

  return result
}

const get = (params?: BookGetArgs): Promise<BookPagedResultDto> => {
  return useApollo().query({
    variables: {
      ...params
    },
    query: gql`
      query GetBooks (
        $needle: String,
        $skip: Int,
        $take: Int,
        $sortBy: String,
        $descending: Boolean,
        $isDraft: Boolean,
        $allowImportedDrafts: Boolean,
        $hasReviews: Boolean
        $missingPreview: Boolean
      ) {
        books (
          needle: $needle,
          skip: $skip,
          take: $take,
          sortBy: $sortBy,
          descending: $descending,
          isDraft: $isDraft,
          allowImportedDrafts: $allowImportedDrafts,
          hasReviews: $hasReviews
          missingPreview: $missingPreview
        ) {
          totalRows,
          items {
            ...BookFragment
          }
        }
      }
      ${bookFragments.book}
    `
  })
}

const getById = (id?: number, args?: BookGetByIdArgs): Promise<BookDto | null> => {
  if (id === void 0) {
    return Promise.reject(new Error('Missing id.'))
  }

  return useApollo().query({
    variables: {
      id,
      ...args
    },
    query: gql`
      query GetBook (
        $id: Int!,
        $genericSearch: Boolean
      ) {
        book (id: $id, genericSearch: $genericSearch) {
          ...BookFragment
        }
      }
      ${bookFragments.book}
    `
  }).then((book: any) => {
    return getBookResult(book)
  })
}

const create = (dto: CreateBookDto): Promise<BookDto> => {
  return useApollo().mutate({
    variables: {
      ...baseBookVars(dto)
    },
    mutation: gql`
      mutation CreateBook (
        $title: String!,
        $description: String,
        $penName: String,
        $authorPenNameId: Int,
        $penNameType: Int,
        $bookCover: String,
        $releaseDate: DateTime,
        $seriesName: String,
        $seriesNumber: Int,
        $categories: [CategoryIdInput!],
        $keywords: String,
        $wordCount: Int,
        $audience: Int,
        $language: Int,
        $linkAmazonAsin: String,
        $linkAmazonAsinPrint: String,
        $linkGoodreads: String,
        $linkBookbub: String,
        $linkBarnes: String,
        $linkGoogle: String,
        $linkKobo: String,
        $linkSmashwords: String,
        $smashwordsCouponCode: String,
        $linkITunes: String,
        $linkAudibleUs: String,
        $linkAudibleUk: String,
        $linkITunesAudio: String,
        $linkBarnesAudio: String,
        $linkGoogleAudio: String,
        $linkGoodreadsAudio: String,
        $linkKoboAudio: String,
        $linkBookbubAudio: String,
        $linkStoryGraph: String,
        $linkStoryGraphAudio: String,
        $pdfFile: String,
        $pdfFileSizeBytes: Int,
        $pdfSystemGenerated: Boolean,
        $pdfFileFriendlyName: String,
        $epubFile: String,
        $epubFileSizeBytes: Int,
        $epubPreviewFile: String,
        $epubSystemGenerated: Boolean
        $epubFileFriendlyName: String
      ) {
        createBook (
          title: $title,
          description: $description,
          penName: $penName,
          authorPenNameId: $authorPenNameId,
          penNameType: $penNameType,
          bookCover: $bookCover,
          releaseDate: $releaseDate,
          seriesName: $seriesName,
          seriesNumber: $seriesNumber,
          categories: $categories,
          keywords: $keywords,
          wordCount: $wordCount,
          audience: $audience,
          language: $language,
          linkAmazonAsin: $linkAmazonAsin,
          linkAmazonAsinPrint: $linkAmazonAsinPrint,
          linkGoodreads: $linkGoodreads,
          linkBookbub: $linkBookbub,
          linkBarnes: $linkBarnes,
          linkGoogle: $linkGoogle,
          linkKobo: $linkKobo,
          linkSmashwords: $linkSmashwords,
          smashwordsCouponCode: $smashwordsCouponCode,
          linkITunes: $linkITunes,
          linkAudibleUs: $linkAudibleUs,
          linkAudibleUk: $linkAudibleUk,
          linkITunesAudio: $linkITunesAudio,
          linkBarnesAudio: $linkBarnesAudio,
          linkGoogleAudio: $linkGoogleAudio,
          linkGoodreadsAudio: $linkGoodreadsAudio,
          linkKoboAudio: $linkKoboAudio,
          linkBookbubAudio: $linkBookbubAudio,
          linkStoryGraph: $linkStoryGraph,
          linkStoryGraphAudio: $linkStoryGraphAudio,
          pdfFile: $pdfFile,
          pdfFileSizeBytes: $pdfFileSizeBytes,
          pdfSystemGenerated: $pdfSystemGenerated,
          pdfFileFriendlyName: $pdfFileFriendlyName,
          epubFile: $epubFile,
          epubFileSizeBytes: $epubFileSizeBytes,
          epubPreviewFile: $epubPreviewFile,
          epubSystemGenerated: $epubSystemGenerated
          epubFileFriendlyName: $epubFileFriendlyName
        ) {
          ...BookFragment
        }
      }
      ${bookFragments.book}
    `
  }).then((book: any) => getBookResult(book) || new BookDto()) // TODO: Why return new one if not updated?
}

const update = (model: UpdateBookDto): Promise<BookDto> => {
  return useApollo().mutate({
    variables: {
      ...baseBookVars(model),
      id: model.id
    },
    mutation: gql`
      mutation UpdateBook (
        $id: Int!,
        $title: String,
        $description: String,
        $penName: String,
        $authorPenNameId: Int,
        $penNameType: Int
        $bookCover: String,
        $releaseDate: DateTime,
        $seriesName: String,
        $seriesNumber: Int,
        $categories: [CategoryIdInput!],
        $keywords: String,
        $wordCount: Int,
        $audience: Int,
        $language: Int,
        $linkAmazonAsin: String,
        $linkAmazonAsinPrint: String,
        $linkGoodreads: String,
        $linkBookbub: String,
        $linkBarnes: String,
        $linkGoogle: String,
        $linkKobo: String,
        $linkSmashwords: String,
        $smashwordsCouponCode: String,
        $linkITunes: String,
        $linkAudibleUs: String,
        $linkAudibleUk: String,
        $linkITunesAudio: String,
        $linkBarnesAudio: String,
        $linkGoogleAudio: String,
        $linkGoodreadsAudio: String,
        $linkKoboAudio: String,
        $linkBookbubAudio: String,
        $linkStoryGraph: String,
        $linkStoryGraphAudio: String,
        $pdfFile: String,
        $pdfFileSizeBytes: Int,
        $pdfSystemGenerated: Boolean,
        $pdfFileDate: DateTime,
        $pdfFileFriendlyName: String,
        $epubFile: String,
        $epubFileSizeBytes: Int,
        $epubPreviewFile: String,
        $epubSystemGenerated: Boolean,
        $epubFileDate: DateTime
        $epubFileFriendlyName: String
      ) {
        updateBook (
          id: $id,
          title: $title,
          description: $description,
          penName: $penName,
          authorPenNameId: $authorPenNameId,
          penNameType: $penNameType,
          bookCover: $bookCover,
          releaseDate: $releaseDate,
          seriesName: $seriesName,
          seriesNumber: $seriesNumber,
          categories: $categories,
          keywords: $keywords,
          wordCount: $wordCount,
          audience: $audience,
          language: $language,
          linkAmazonAsin: $linkAmazonAsin,
          linkAmazonAsinPrint: $linkAmazonAsinPrint,
          linkGoodreads: $linkGoodreads,
          linkBookbub: $linkBookbub,
          linkBarnes: $linkBarnes,
          linkGoogle: $linkGoogle,
          linkKobo: $linkKobo,
          linkSmashwords: $linkSmashwords,
          smashwordsCouponCode: $smashwordsCouponCode,
          linkITunes: $linkITunes,
          linkAudibleUs: $linkAudibleUs,
          linkAudibleUk: $linkAudibleUk,
          linkITunesAudio: $linkITunesAudio,
          linkBarnesAudio: $linkBarnesAudio,
          linkGoogleAudio: $linkGoogleAudio,
          linkGoodreadsAudio: $linkGoodreadsAudio,
          linkKoboAudio: $linkKoboAudio,
          linkBookbubAudio: $linkBookbubAudio,
          linkStoryGraph: $linkStoryGraph,
          linkStoryGraphAudio: $linkStoryGraphAudio,
          pdfFile: $pdfFile,
          pdfFileSizeBytes: $pdfFileSizeBytes,
          pdfSystemGenerated: $pdfSystemGenerated,
          pdfFileDate: $pdfFileDate,
          pdfFileFriendlyName: $pdfFileFriendlyName,
          epubFile: $epubFile,
          epubFileSizeBytes: $epubFileSizeBytes,
          epubPreviewFile: $epubPreviewFile,
          epubSystemGenerated: $epubSystemGenerated,
          epubFileDate: $epubFileDate
          epubFileFriendlyName: $epubFileFriendlyName
        ) {
          ...BookFragment
        }
      }
      ${bookFragments.book}
    `
  }).then((book: any) => getBookResult(book) || new BookDto()) // TODO: Why return new one if not updated?
}

/**
 * The book's Apple Books pool. Every one of these returns the pool as it now stands, so the caller
 * never has to reconcile a local list against what the server did with duplicates.
 */
const getAppleBooksCodes = (bookId: number): Promise<BookAppleBooksCodeDto[]> => {
  return useApollo().query({
    variables: {
      bookId
    },
    query: gql`
      query GetBookAppleBooksCodes ($bookId: Int!) {
        bookAppleBooksCodes (bookId: $bookId) {
          ...BookAppleBooksCodeFragment
        }
      }
      ${bookFragments.appleBooksCode}
    `
  })
}

/** `expirationDate` is a calendar day, `YYYY-MM-DD` — see BookAppleBooksCodeDto. */
const addAppleBooksCodes = (
  bookId: number,
  codes: string[],
  expirationDate?: string
): Promise<BookAppleBooksCodeDto[]> => {
  return useApollo().mutate({
    variables: {
      bookId,
      codes,
      expirationDate
    },
    mutation: gql`
      mutation AddBookAppleBooksCodes (
        $bookId: Int!,
        $codes: [String!]!,
        $expirationDate: String
      ) {
        addBookAppleBooksCodes (
          bookId: $bookId,
          codes: $codes,
          expirationDate: $expirationDate
        ) {
          ...BookAppleBooksCodeFragment
        }
      }
      ${bookFragments.appleBooksCode}
    `
  })
}

const removeAppleBooksCode = (bookId: number, id: number): Promise<BookAppleBooksCodeDto[]> => {
  return useApollo().mutate({
    variables: {
      bookId,
      id
    },
    mutation: gql`
      mutation RemoveBookAppleBooksCode (
        $bookId: Int!,
        $id: Int!
      ) {
        removeBookAppleBooksCode (
          bookId: $bookId,
          id: $id
        ) {
          ...BookAppleBooksCodeFragment
        }
      }
      ${bookFragments.appleBooksCode}
    `
  })
}

/**
 * Clear out the codes that have run past their expiry. The server decides which those are - it also
 * spares any that a reader has already claimed - so this takes nothing but the book.
 */
const removeExpiredAppleBooksCodes = (bookId: number): Promise<BookAppleBooksCodeDto[]> => {
  return useApollo().mutate({
    variables: {
      bookId
    },
    mutation: gql`
      mutation RemoveExpiredBookAppleBooksCodes ($bookId: Int!) {
        removeExpiredBookAppleBooksCodes (bookId: $bookId) {
          ...BookAppleBooksCodeFragment
        }
      }
      ${bookFragments.appleBooksCode}
    `
  })
}

const checkDelete = (id: number): Promise<EntityDeletionSummaryDto[]> => {
  return useApollo().query({
    variables: {
      id
    },
    query: gql`
      query CheckDeleteBook ($id: Int!) {
        checkDeleteBook (id: $id) {
          count,
          dtoTypeName,
          ids
        }
      }
    `
  })
}

const deleteBook = (id?: number): Promise<any> => {
  if (id === void 0) {
    throw new Error('Missing id.')
  }

  return useApollo().mutate({
    variables: {
      id
    },
    mutation: gql`
      mutation ConfirmDeleteBook (
        $id: Int!
      ) {
        confirmDeleteBook(id: $id) {
          id
        }
      }
    `
  })
}

const getImportOptions = (penName: string, needle: string): Promise<BookDto[]> => {
  return useAxios().axios().post('book/amazon-books-to-import', {
    penName,
    needle
  }).then((res: any) => {
    return res.data
  })
}

/**
 * The API renders the Amazon page through PhantomJsCloud for this, so it takes seconds. `silent` keeps it
 * away from the loading overlay, for a fetch started before anyone is waiting on it.
 */
const fetchAmazonBookDescription = (asin: string, options: { silent?: boolean } = {}): Promise<string | undefined> => {
  return useAxios().axios().post('book/amazon-book-description', {
    asin
  }, options.silent ? withoutLoader() : void 0).then((res: any) => {
    return res.data?.description
  })
}

const getBooksGeneric = (args: BookGetArgs): Promise<BookPagedResultDto> => {
  return useApollo().query({
    variables: {
      ...args
    },
    query: gql`
      query getBooksGeneric (
        $needle: String,
        $skip: Int,
        $take: Int,
        $sortBy: String,
        $descending: Boolean,
        $penNameId: Int,
        $seriesName: String,
        $beforeSeriesNumber: Int
      ) {
        booksGeneric (
          needle: $needle,
          skip: $skip,
          take: $take,
          sortBy: $sortBy,
          descending: $descending,
          penNameId: $penNameId,
          seriesName: $seriesName,
          beforeSeriesNumber: $beforeSeriesNumber
        ) {
          totalRows,
          items {
            ...BookFragment
          }
        }
      }
      ${bookFragments.book}
    `
  })
}

const getTags = (args: BookGetTagsArgs): Promise<BookTagDto[]> => {
  return useApollo().query({
    variables: {
      ...args
    },
    query: gql`
      query GetBookTagsForCategory (
        $categoryId: Int,
        $searchValue: String,
        $skip: Int,
        $take: Int
      ) {
        bookTagsForCategory (
          searchValue: $searchValue,
          skip: $skip,
          take: $take,
          categoryId: $categoryId
        ) {
          name,
          count
        }
      }
    `
  })
}

const splitKeywords = (book: BookDto | undefined): string[] => {
  return [...new Set(book?.keywords?.split(','))]
}

const getAffiliateLink = (book: BookDto, site: ArcSite): string => {
  const { ...sites } = bsConstants.ARCS.REVIEWS.FLAGS
  switch (site.site) {
    case sites.AMAZON: return `https://www.amazon.com/dp/${book.linkAmazonAsin}?tag=booksprout09-20`
    case sites.ITUNES: return book.linkITunes + '?at=1001l9EK'
    default: return (book as HasIndex)[site.fieldName]
  }
}


const convertFile = (book: BookDto, fileExt: string): Promise<ArcConvertedFileDto> => {
  let fromPath: string | undefined = ''
  if (validStr(book.epubFile)) {
    fromPath = book.epubFile
  } else if (validStr(book.pdfFile)) {
    fromPath = book.pdfFile
  }

  if (fromPath !== void 0) {
    const params: ArcConvertFileDto = {
      id: book.id.toString() || '-1',
      convertIntoFileExt: fileExt.startsWith('.') ? fileExt : '.' + fileExt,
      sourceFilePath: fromPath
    }

    return useAxios().axios().post('book/generate-file', params, {
      timeout: 300000
    }).then((r: { data: ArcConvertedFileDto }) => {
      return r.data
    })
  } else {
    return Promise.reject()
  }
}

const addFileFactory = (files: any[], model: BookDto): object => {
  return {
    url: crud.getApiControllerUrl('add-file'),
    withCredentials: true,
    headers: [
      { name: 'Authorization', value: `Bearer ${authStoreGetters.authToken}` },
      { name: 'CSRF-Token', value: authStoreGetters.csrfToken }
    ],
    formFields: [
      // AddFileDto shape
      { name: 'id', value: model.id === void 0 ? -1 : model.id },
      { name: 'fileName', value: files[0].name }
    ]
  }
}

const generateEpubPreview = (dto: GenerateEpubPreviewDto): Promise<EpubPreviewDto> => {
  return useAxios().axios().post('book/generate-epub-preview', dto, {
    timeout: 300000
  }).then((r: { data: EpubPreviewDto }) => {
    return r.data
  })
}

const removeRemoteFile = (id: number, fileExt: string): Promise<any> => {
  return useAxios().axios().post('book/remove-remote-file', {
    id,
    fileExt
  })
}

const stopEpubPreviewGeneration = (bookId: number): Promise<any> => {
  return useAxios().axios().post('book/stop-generate-epub-preview', {
    bookId
  })
}

/**
 * `globalLoader` is chained by the book list, so it hands back this bag rather than apollo's.
 */
const service = {
  ...crud,

  bookFragments,

  get,
  getById,
  create,
  update,
  getAppleBooksCodes,
  addAppleBooksCodes,
  removeAppleBooksCode,
  removeExpiredAppleBooksCodes,
  checkDelete,
  delete: deleteBook,
  getImportOptions,
  fetchAmazonBookDescription,
  getBooksGeneric,
  getTags,
  splitKeywords,
  getAffiliateLink,
  convertFile,
  addFileFactory,
  generateEpubPreview,
  removeRemoteFile,
  stopEpubPreviewGeneration,

  globalLoader (enable: boolean) {
    crud.setGlobalLoader(enable)
    return service
  }
}

export function bookService () {
  return service
}
